const MAX_DIMENSION = 1280;
// Firestore caps a document at 1MB total, and base64 inflates raw bytes
// by ~33%. Targeting 650KB of raw compressed bytes lands around ~865KB
// once encoded - safely under the cap with real room to spare for the
// message's other fields and Firestore's own per-document overhead.
const TARGET_MAX_BYTES = 650 * 1024;
const MIN_QUALITY = 0.4;
const QUALITY_STEP = 0.12;

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read that image.'));
    img.src = dataUrl;
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

function approxBytesFromDataUrl(dataUrl) {
  // Rough but reliable enough estimate of decoded byte size from a
  // base64 data URL's string length.
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  return Math.ceil((base64.length * 3) / 4);
}

/**
 * compressImage
 * Resizes an image file down to at most MAX_DIMENSION on its longest
 * side, then re-encodes as JPEG, stepping quality down until it fits
 * under TARGET_MAX_BYTES. Resolves with a data URL string ready to
 * store directly on a Firestore message document. Rejects with a
 * plain, user-facing error message if the file isn't an image, can't
 * be read, or is still too large even at the lowest quality setting.
 */
export default async function compressImage(file) {
  if (!file.type || !file.type.startsWith('image/')) {
    throw new Error('That file is not an image.');
  }

  const original = await readFileAsDataUrl(file);
  const img = await loadImage(original);

  let { width, height } = img;
  if (width > height && width > MAX_DIMENSION) {
    height = Math.round((height * MAX_DIMENSION) / width);
    width = MAX_DIMENSION;
  } else if (height > MAX_DIMENSION) {
    width = Math.round((width * MAX_DIMENSION) / height);
    height = MAX_DIMENSION;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error("Your browser can't process images here.");
  }
  ctx.drawImage(img, 0, 0, width, height);

  let quality = 0.78;
  let dataUrl = canvas.toDataURL('image/jpeg', quality);

  while (approxBytesFromDataUrl(dataUrl) > TARGET_MAX_BYTES && quality > MIN_QUALITY) {
    quality = Math.max(MIN_QUALITY, quality - QUALITY_STEP);
    dataUrl = canvas.toDataURL('image/jpeg', quality);
  }

  if (approxBytesFromDataUrl(dataUrl) > TARGET_MAX_BYTES) {
    throw new Error('This image is too large even after compression. Try a smaller photo.');
  }

  return dataUrl;
}